import React, { useState, useRef, useEffect } from 'react';
import { Video, Mic, MicOff, VideoOff, X, Loader, Copy, Check } from 'lucide-react';
import { Account } from '../types';
import { useTwilioVideo } from '../hooks/useTwilioVideo';
import { useHealthScribeStreaming } from '../hooks/useHealthScribeStreaming';
import { RemoteParticipantView } from './RemoteParticipantView';
import { api } from '../services/api';
import { requestWakeLock, releaseWakeLock } from '../utils/screenWakeLock';

interface EncounterVideoProps {
  account?: Account;
  patientId: string;
  noteTemplate?: string;
  taskDefinitionId?: string;
  selectedMicId?: string;
  roomName?: string; // Optional: if provided, join this specific room (for shared links)
  onClose?: () => void;
  onRecordingComplete?: (userRequestId: string) => void;
}

export const EncounterVideo: React.FC<EncounterVideoProps> = ({
  account,
  patientId,
  noteTemplate = 'PHYSICAL_SOAP',
  taskDefinitionId,
  selectedMicId,
  roomName: providedRoomName,
  onClose,
  onRecordingComplete,
}) => {
  const { 
    isConnected, 
    participants, 
    localVideoTrack,
    connectToRoom, 
    addLocalVideo, 
    addLocalAudio,
    toggleMute,
    toggleVideo,
    leaveRoom 
  } = useTwilioVideo();

  // HealthScribe streaming integration (same as RecordAudioSection)
  const {
    session,
    isConnected: isHealthScribeConnected,
    isRecording,
    startSession: startHealthScribeSession,
    startRecording: startHealthScribeRecording,
    stopRecording: stopHealthScribeRecording,
    stopSession: stopHealthScribeSession,
    initializeConnection: initializeHealthScribeConnection,
  } = useHealthScribeStreaming();

  const [joining, setJoining] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [error, setError] = useState<string>('');
  const [shareLink, setShareLink] = useState<string>('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const localVideoRef = useRef<HTMLDivElement>(null);
  const hasInitializedHealthScribe = useRef(false);
  const isConnectedRef = useRef(false);
  const completedRequestIdsRef = useRef<Set<string>>(new Set());

  // Initialize HealthScribe connection when component mounts (only once)
  useEffect(() => {
    if (hasInitializedHealthScribe.current) return;
    
    const init = async () => {
      hasInitializedHealthScribe.current = true;
      try {
        await initializeHealthScribeConnection();
      } catch (err) {
        console.error('Failed to initialize HealthScribe:', err);
      }
    };
    init();
  }, [initializeHealthScribeConnection]);

  // Track session completion and trigger callback (only once per request)
  useEffect(() => {
    if (session?.status === 'completed' && onRecordingComplete && session.userRequestId) {
      // Prevent duplicate callbacks for the same request
      if (!completedRequestIdsRef.current.has(session.userRequestId)) {
        completedRequestIdsRef.current.add(session.userRequestId);
        console.log('✅ HealthScribe session completed, triggering callback');
        onRecordingComplete(session.userRequestId);
      }
    }
  }, [session?.status, session?.userRequestId, onRecordingComplete]);

  // Track connection state in ref to avoid stale closures
  useEffect(() => {
    isConnectedRef.current = isConnected;
  }, [isConnected]);

  // Auto-join if room name is provided (joining via shared link)
  useEffect(() => {
    if (providedRoomName && account && isHealthScribeConnected && !isConnected && !joining) {
      console.log('🔗 Auto-joining room from shared link:', providedRoomName);
      handleJoin().catch(err => {
        console.error('Failed to auto-join room:', err);
        setError('Failed to join video call. Please try again.');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providedRoomName, account, isHealthScribeConnected, isConnected, joining]);

  // Cleanup on unmount only (empty deps array)
  useEffect(() => {
    return () => {
      // Clean up video and HealthScribe on unmount
      // Use ref to check current state
      if (isConnectedRef.current) {
        leaveRoom();
      }
      releaseWakeLock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on unmount

  // Detect mobile/desktop viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Attach local video track to DOM
  useEffect(() => {
    const container = localVideoRef.current;
    if (!container || !localVideoTrack) return;

    const videoElement = localVideoTrack.attach();
    // Style video element to fill container
    if (videoElement instanceof HTMLVideoElement) {
      videoElement.style.width = '100%';
      videoElement.style.height = '100%';
      videoElement.style.objectFit = 'cover';
    }
    container.appendChild(videoElement);

    return () => {
      const elements = localVideoTrack.detach();
      elements.forEach((element) => {
        if (element.parentNode === container) {
          container.removeChild(element);
        }
      });
    };
  }, [localVideoTrack]);

  const handleJoin = async () => {
    if (!account) {
      setError('Account information required');
      return;
    }

    setJoining(true);
    setError('');

    try {
      // Request wake lock to keep screen on during video call
      const wakeLockAcquired = await requestWakeLock();
      if (!wakeLockAcquired) {
        console.warn('Wake lock not acquired, screen may dim during video call');
      }

      // Get the device ID - we'll use the same mic for both HealthScribe and Twilio
      const micDeviceId = selectedMicId || undefined;

      // STEP 1: Start HealthScribe session and recording (only if we're creating the room, not joining)
      // Only the room creator should start HealthScribe for transcription
      const isRoomCreator = !providedRoomName;
      
      if (isRoomCreator) {
        // Check if taskDefinitionId is provided - required for UserRequest creation
        if (!taskDefinitionId) {
          throw new Error('Task Definition ID is required to create a visit. Please provide taskDefinitionId in the URL or select a template.');
        }
        
        console.log('🎬 Starting HealthScribe session for video call (room creator)...');
        await startHealthScribeSession(patientId, noteTemplate, account, taskDefinitionId);
        
        // Small delay to ensure session state is updated
        await new Promise(resolve => setTimeout(resolve, 100));

        // Start HealthScribe recording (this gets the microphone stream)
        await startHealthScribeRecording(micDeviceId);
        console.log('✅ HealthScribe recording started');
      } else {
        console.log('👤 Joining existing room - HealthScribe will not be started (only room creator transcribes)');
      }

      // STEP 2: Join Twilio video room
      // Use provided room name if joining existing room, otherwise create new one
      const generatedRoomName = providedRoomName || `encounter-${patientId}-${Date.now()}`;
      console.log('📹 Joining room:', generatedRoomName);
      
      // Generate shareable link (only if we created the room, not if joining)
      if (!providedRoomName) {
        const baseUrl = window.location.origin;
        // Include taskDefinitionId in share link for context (though participants won't create UserRequest)
        const shareParams = new URLSearchParams();
        shareParams.set('room', generatedRoomName);
        shareParams.set('patientId', patientId);
        if (taskDefinitionId) {
          shareParams.set('taskDefinitionId', taskDefinitionId);
        }
        const shareUrl = `${baseUrl}/video?${shareParams.toString()}`;
        setShareLink(shareUrl);
      }
      
      // Get identity from account email or generate fallback
      let identity: string;
      if (account.emailAddresses && account.emailAddresses.length > 0) {
        identity = account.emailAddresses[0];
      } else {
        identity = `clinician-${Date.now()}`;
      }

      // Fetch token from backend
      const { token } = await api.getVideoToken(identity, generatedRoomName);

      // Connect to room
      await connectToRoom(token, generatedRoomName);

      // Add local video and audio (use same mic device as HealthScribe)
      // Small delay to ensure room is fully connected before adding tracks
      await new Promise(resolve => setTimeout(resolve, 100));
      
      await addLocalVideo();
      await addLocalAudio(micDeviceId);

      setIsVideoEnabled(true);
      setIsMuted(false);
      setError(''); // Clear any previous errors
      console.log('✅ Video call started with HealthScribe streaming');
    } catch (err) {
      console.error('Failed to join video room:', err);
      let errorMessage = err instanceof Error ? err.message : 'Failed to join video call';
      
      // Provide helpful error message for Twilio configuration issues
      if (errorMessage.includes('Twilio configuration')) {
        errorMessage = 'Video calling is not configured. Please contact your administrator to set up Twilio credentials.';
      }
      
      setError(errorMessage);
      // Clean up on error (only if we started HealthScribe)
      try {
        if (!providedRoomName && isRecording) {
          await stopHealthScribeRecording();
        }
        if (!providedRoomName) {
          await stopHealthScribeSession();
        }
      } catch (cleanupErr) {
        console.error('Error during cleanup:', cleanupErr);
      }
      await releaseWakeLock();
    } finally {
      setJoining(false);
    }
  };

  const handleLeave = async () => {
    try {
      setError('Processing clinical notes...');
      
      // STEP 1: Stop Twilio video first
      leaveRoom();
      
      // STEP 2: Stop HealthScribe recording and session (only if we started it - room creator)
      // Only the room creator should stop HealthScribe to trigger note generation
      const isRoomCreator = !providedRoomName;
      if (isRoomCreator) {
        if (isRecording) {
          console.log('🛑 Stopping HealthScribe recording...');
          await stopHealthScribeRecording();
        }
        
        console.log('🛑 Stopping HealthScribe session...');
        await stopHealthScribeSession();
      } else {
        console.log('👤 Leaving room as participant - HealthScribe will continue (room creator manages it)');
      }
      
      // Release wake lock
      await releaseWakeLock();
      
      setError('');
      console.log('✅ Video call ended, HealthScribe session stopped');
      
      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error('Error leaving video call:', err);
      setError(err instanceof Error ? err.message : 'Failed to end video call');
      // Ensure wake lock is released even on error
      await releaseWakeLock();
    }
  };

  const handleToggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    toggleMute(newMuted);
  };

  const handleToggleVideo = () => {
    const newEnabled = !isVideoEnabled;
    setIsVideoEnabled(newEnabled);
    toggleVideo(newEnabled);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      // Fallback: select text
      const textArea = document.createElement('textarea');
      textArea.value = shareLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  if (!isConnected) {
    return (
      <div className="space-y-4">
        <div className="bg-base-200 rounded-lg p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-primary/30 rounded-full flex items-center justify-center mb-4 mx-auto">
              <Video className="w-8 h-8 text-primary-content" />
            </div>
            <h2 className="text-xl font-semibold text-base-content mb-2">Video Conference</h2>
            <p className="text-base-content/70 mb-6">
              Join a video call for this encounter. Audio will be transcribed automatically.
            </p>
            
            {!isHealthScribeConnected && (
              <div className="alert alert-info mb-4">
                <Loader className="w-4 h-4 animate-spin" />
                <span>Initializing transcription service...</span>
              </div>
            )}
            
            {!taskDefinitionId && !providedRoomName && (
              <div className="alert alert-warning mb-4">
                <span>⚠️ Task Definition ID is required to create a visit. Please add <code>?taskDefinitionId=YOUR_ID</code> to the URL.</span>
              </div>
            )}
            
            <button
              onClick={handleJoin}
              disabled={joining || !account || !isHealthScribeConnected || (!taskDefinitionId && !providedRoomName)}
              className="btn btn-primary btn-lg"
            >
              {joining ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  {providedRoomName ? 'Joining video call...' : 'Starting video call...'}
                </>
              ) : (
                <>
                  <Video className="w-5 h-5 mr-2" />
                  {providedRoomName ? 'Join Video Call' : 'Start Video Call'}
                </>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }

  const hasRemoteParticipants = participants.length > 0;
  const mainParticipant = participants[0]; // First remote participant is main

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] md:h-[calc(100vh-4rem)]">
      {/* Share Link Section - Collapsible and less prominent during call */}
      {isConnected && shareLink && (
        <div className="bg-base-200/80 backdrop-blur-sm rounded-lg p-3 mb-4 border border-base-300">
          <details className="collapse collapse-arrow">
            <summary className="collapse-title text-sm font-medium text-base-content/70 p-0 min-h-0">
              <div className="flex items-center gap-2">
                <Copy className="w-4 h-4" />
                <span>Share call link</span>
              </div>
            </summary>
            <div className="collapse-content p-0 pt-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={shareLink}
                  className="input input-bordered flex-1 text-xs h-8"
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <button
                  onClick={handleCopyLink}
                  className={`btn btn-xs ${linkCopied ? 'btn-success' : 'btn-outline'}`}
                  title="Copy link"
                >
                  {linkCopied ? (
                    <>
                      <Check className="w-3 h-3 mr-1" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3 mr-1" />
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>
          </details>
        </div>
      )}

      {/* Main Video Container */}
      <div className="flex-1 relative bg-base-300 rounded-lg overflow-hidden mb-4">
        {/* Desktop: Main video (remote) with local video PIP */}
        {/* Mobile: Main video (local or remote) with remote PIP if exists */}
        {!isMobile ? (
          // Desktop Layout
          <>
            {/* Main Video - Remote Participant or Waiting */}
            <div className="absolute inset-0 bg-base-200">
              {hasRemoteParticipants ? (
                <RemoteParticipantView 
                  participant={mainParticipant} 
                  isMain={true}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-base-content/50 text-center">
                    <Video className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">Waiting for participants...</p>
                    <p className="text-sm mt-2">Share the link above to invite others</p>
                  </div>
                </div>
              )}
            </div>

            {/* Local Video - PIP in bottom right corner */}
            <div className="absolute bottom-4 right-4 w-64 h-48 rounded-lg overflow-hidden bg-base-200 shadow-2xl border-2 border-base-100">
              <div ref={localVideoRef} className="w-full h-full object-cover" />
              {!isVideoEnabled && (
                <div className="absolute inset-0 flex items-center justify-center bg-base-300">
                  <VideoOff className="w-8 h-8 text-base-content/50" />
                </div>
              )}
              <div className="absolute bottom-2 left-2 bg-black/70 backdrop-blur-sm text-white px-2 py-1 rounded text-xs font-medium">
                You {isMuted && '🔇'}
              </div>
            </div>
          </>
        ) : (
          // Mobile Layout
          <>
            {/* Main Video - Remote when participants exist, local when waiting */}
            {hasRemoteParticipants ? (
              <>
                <div className="absolute inset-0 bg-base-200">
                  <RemoteParticipantView 
                    participant={mainParticipant} 
                    isMain={true}
                  />
                </div>
                {/* Local video PIP in top right */}
                <div className="absolute top-4 right-4 w-32 h-40 rounded-lg overflow-hidden bg-base-200 shadow-xl border-2 border-base-100 z-10">
                  <div ref={localVideoRef} className="w-full h-full object-cover" />
                  {!isVideoEnabled && (
                    <div className="absolute inset-0 flex items-center justify-center bg-base-300">
                      <VideoOff className="w-6 h-6 text-base-content/50" />
                    </div>
                  )}
                  <div className="absolute bottom-1 left-1 bg-black/70 text-white px-1.5 py-0.5 rounded text-xs">
                    You {isMuted && '🔇'}
                  </div>
                </div>
              </>
            ) : (
              <div className="absolute inset-0 bg-base-200">
                <div ref={localVideoRef} className="w-full h-full object-cover" />
                {!isVideoEnabled && (
                  <div className="absolute inset-0 flex items-center justify-center bg-base-300">
                    <VideoOff className="w-16 h-16 text-base-content/50" />
                  </div>
                )}
                <div className="absolute bottom-4 left-4 bg-black/70 backdrop-blur-sm text-white px-3 py-2 rounded-lg text-sm font-medium">
                  You {isMuted && '🔇'}
                </div>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-base-content/50 text-center bg-base-200/80 backdrop-blur-sm rounded-lg p-4">
                    <Video className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Waiting for participants...</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Additional participants indicator (if more than 1) */}
        {participants.length > 1 && (
          <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-sm text-white px-3 py-2 rounded-lg text-sm font-medium">
            {participants.length} participants
          </div>
        )}
      </div>

      {/* Controls Bar */}
      <div className="flex items-center justify-center gap-3 pb-4">
        <button
          onClick={handleToggleMute}
          className={`btn btn-circle btn-lg ${isMuted ? 'btn-error' : 'btn-ghost hover:btn-primary'}`}
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
        </button>

        <button
          onClick={handleToggleVideo}
          className={`btn btn-circle btn-lg ${!isVideoEnabled ? 'btn-error' : 'btn-ghost hover:btn-primary'}`}
          title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
        >
          {isVideoEnabled ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
        </button>

        <button
          onClick={handleLeave}
          className="btn btn-circle btn-lg btn-error"
          title="Leave call"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Status Bar */}
      <div className="flex flex-col gap-2">
        {/* HealthScribe Status */}
        {isRecording && (
          <div className="alert alert-info py-2">
            <span className="text-sm">🎤 Recording and transcribing audio...</span>
          </div>
        )}

        {error && (
          <div className="alert alert-error py-2">
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Session Error Display */}
        {session?.status === 'error' && session.error && (
          <div className="alert alert-error py-2">
            <span className="text-sm">⚠️ Transcription Error: {session.error}</span>
          </div>
        )}
      </div>
    </div>
  );
};

